/**
 * BottomNav.jsx  —  FIXED
 *
 * ROOT CAUSE FIXED (Part 2 — Protected Action UX):
 *   Track (/orders) and Account (/account) are protected routes nested under
 *   <RequireAuth />. Clicking the <Link> components triggered a navigation that
 *   React Router resolved synchronously, rendering RequireAuth which silently
 *   redirected to /login — no feedback to the user at all.
 *
 * FIX STRATEGY:
 *   1. Mark protected nav items with `protected: true` in NAV_ITEMS.
 *   2. Replace <Link> with <button>-driven navigation on protected items.
 *   3. On click: if user is not authenticated, fire toast.error('Please login
 *      to continue') then navigate('/login'). No route flicker, no blank screen.
 *   4. If authenticated, navigate normally.
 *   5. Toast uses `sonner` (same library as Login success/error toasts) — no
 *      new library added.
 *
 * LAYOUT NOTE (Part 1):
 *   BottomNav was already `position: fixed; bottom: 0` in CSS (.bottom-nav).
 *   The perceived movement was caused by the body padding-top jumping when
 *   AnnouncementBar removed the data-announcement attribute — fixed in
 *   AnnouncementBar.jsx + index.css.  No CSS changes needed here.
 */

import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Store, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const NAV_ITEMS = [
  { label: 'Home',    icon: Home,   path: '/',        protected: false },
  { label: 'Shop',    icon: Store,  path: '/shop',    protected: false },
  { label: 'Track',   icon: MapPin, path: '/orders',  protected: true  },
  { label: 'Account', icon: User,   path: '/account', protected: true  },
];

export default function BottomNav() {
  const { pathname }       = useLocation();
  const navigate           = useNavigate();
  const { isAuthenticated } = useAuth();

  // Mirror what AnnouncementBar does — let CSS know BottomNav is mounted
  // so body gets padding-bottom and content never hides behind the nav.
  useEffect(() => {
    document.body.setAttribute('data-bottom-nav', 'true');
    return () => document.body.removeAttribute('data-bottom-nav');
  }, []);

  /**
   * Handles clicks on protected nav items.
   * – Authenticated   → navigate normally.
   * – Unauthenticated → block navigation, show toast, redirect to /login.
   *
   * We save the intended destination in router `state.from` so that after
   * successful login the app can redirect the user back where they wanted to go.
   */
  const handleProtectedNav = (path) => {
    if (!isAuthenticated) {
      toast.error('Please login to continue', {
        id: 'auth-required', // deduplicate: rapid taps only show one toast
      });
      navigate('/login', { state: { from: { pathname: path } }, replace: false });
    } else {
      navigate(path);
    }
  };

  return (
    <nav className="bottom-nav" aria-label="Mobile navigation">
      {NAV_ITEMS.map(({ label, icon: Icon, path, protected: isProtected }) => {
        const isActive =
          path === '/'
            ? pathname === '/'
            : pathname === path ||
              pathname.startsWith(path + '/') ||
              (path === '/shop' && pathname.startsWith('/shop'));

        const sharedClasses = `
          flex-1 flex flex-col items-center justify-center gap-0.5
          text-[10px] font-medium tracking-wide transition-colors
          ${isActive ? 'text-[#D4AF37]' : 'text-gray-500 hover:text-gray-800'}
        `;

        // Protected items — button-driven so we can intercept before routing
        if (isProtected) {
          return (
            <button
              key={label}
              onClick={() => handleProtectedNav(path)}
              className={sharedClasses}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
              <span>{label}</span>
            </button>
          );
        }

        // Public items — plain Link, no auth needed
        return (
          <Link
            key={label}
            to={path}
            className={sharedClasses}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
            <span>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}